import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import RadioButton from "../../../../components/atoms/Radiobutton";
import {
  getJaxVersion,
  getJaxData,
} from "../../../../state/selectors/JaxCkbComponent.selectors";
import {
  renderSingleWithLabel,
  getVarsoUrl,
} from "../../../../common/helpers/helpers";
import Chart from "../../../../components/atoms/charts/Chart";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import JaxCkbContent from "./JaxCkbContent";
import Linkify from "../../../../components/atoms/Linkify";
import conf from "../../../../components/social/linkifyConf";
import { checkAMPClassification, createChartData } from "./helpers";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import HelperText from "../../../../components/atoms/HelperText";

const JaxCkbComponent = (props) => {
  const { jaxVersion, jaxData } = props;
  const [evidenceType, setEvidenceType] = useState("");
  const [options, setOptions] = useState([]);

  const getEvidenceTypes = () => {
    const evidenceTypesOptions = [];
    if (jaxData) {
      const { evidence, extended_evidence: extendedEvidence } = jaxData;
      if (evidence && evidence.length > 0) {
        evidenceTypesOptions.push({
          id: "evidence",
          label: "Variant",
          name: "evidenceType",
          value: "evidence",
        });
      }
      if (extendedEvidence && extendedEvidence.length > 0) {
        evidenceTypesOptions.push({
          id: "extended_evidence",
          label: "Extended",
          name: "evidenceType",
          value: "extended_evidence",
        });
      }
    }
    return evidenceTypesOptions;
  };

  const handleOptionChange = (changeEvent) => {
    setEvidenceType(changeEvent.target.value);
  };

  const checkVariantTypeEvidence = (type) => {
    if (options && options.length > 0) {
      const evidences = options.map((element) => element.id);
      if (evidences.indexOf(type) === -1) {
        return true;
      }
      return false;
    }
    return true;
  };

  useEffect(() => {
    const result = getEvidenceTypes();
    if (!isEmpty(result)) {
      setOptions(result);
      setEvidenceType(result[0].id);
    }
  }, [jaxData]);

  if (!jaxData) return null;

  const {
    evidence,
    extended_evidence: extendedEvidence,
    ckb_id: ckbID,
  } = jaxData;
  const data = jaxData[evidenceType];
  const evidenceSum = data && data.length;
  const ampTierLevel = checkAMPClassification(
    createChartData(data, "amp_tier")
  );
  const titleDetailsRight = ckbID ? (
    <div className="varsome-right">
      <small className="tw-mr-4">
        <a
          href={getVarsoUrl(
            `https://ckb.jax.org/geneVariant/show?geneVariantId=${ckbID}`
          )}
          target="_blank"
        >
          View this on the JAX CKB site
        </a>
      </small>
    </div>
  ) : (
    ""
  );

  const content = (
    <div className="varsome-inner-container">
      <div className="varsome-horiz-flex">
        <div className="varsome-horiz-list-item">
          <div className="varsome-horiz-list-label">
            Gene variant Description
          </div>
          <div className="varsome-horiz-list-value">
            {jaxData.gene_variant_descriptions &&
              jaxData.gene_variant_descriptions.map((obj, i) => {
                return (
                  <Linkify
                    key={`obj-${i}`}
                    text={obj.description}
                    conf={conf}
                    isHtml={false}
                  />
                );
              })}
          </div>
        </div>
      </div>
      <div className="varsome-horiz-flex">
        {options && options.length > 0 && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">
              Evidence Type
              <HelperText fieldDescriptionKey="JAX CKB Evidence Type" />
            </div>
            <div className="varsome-horiz-list-value">
              <div className="radiobuttons radiobuttons--inline">
                <RadioButton
                  id="evidence"
                  key="evidence"
                  label="Variant"
                  name="evidenceType"
                  value="evidence"
                  disabled={checkVariantTypeEvidence("evidence")}
                  isSelected={evidenceType === "evidence"}
                  handleSelection={handleOptionChange}
                />
                <RadioButton
                  id="extended_evidence"
                  key="extended_evidence"
                  label="Extended"
                  name="evidenceType"
                  value="extended_evidence"
                  disabled={checkVariantTypeEvidence("extended_evidence")}
                  isSelected={evidenceType === "extended_evidence"}
                  handleSelection={handleOptionChange}
                />
              </div>
            </div>
          </div>
        )}
        {evidenceSum && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">Evidence Count</div>
            <div className="varsome-horiz-list-value">{evidenceSum}</div>
          </div>
        )}
        <div className="varsome-horiz-list-item">
          <div className="varsome-horiz-list-label">
            Somatic Variant Classification Tier
          </div>
          <div className="varsome-horiz-list-value">
            <span style={{ fontWeight: "bold", color: ampTierLevel.color }}>
              {ampTierLevel.tierLevel}
            </span>
          </div>
        </div>
        {jaxData.coding_impact &&
          renderSingleWithLabel(jaxData, "coding_impact", "Coding Impact")}
        {jaxData.protein_effect &&
          renderSingleWithLabel(jaxData, "protein_effect", "Protein Effect")}
      </div>
      <div className="charts-container">
        <Chart
          showEmptyState={false}
          data={createChartData(data, "amp_tier")}
          title="Somatic Variant Classification Tiers"
        />
        <Chart
          showEmptyState={false}
          data={createChartData(data, "approval_status")}
          title="Approval Status"
        />
        <Chart
          showEmptyState={false}
          data={createChartData(data, "evidence_type")}
          title="Evidence Type"
        />
        <Chart
          showEmptyState={false}
          data={createChartData(data, "indication")}
          title="Cancer Type"
        />
        <Chart
          showEmptyState={false}
          data={createChartData(data, "response_type")}
          title="Response Type"
        />
        <Chart
          showEmptyState={false}
          data={createChartData(data, "therapy")}
          title="Therapy"
        />
      </div>
      {evidence && evidence.length && (
        <div className="tw-mt-8">
          <h5>Evidence Results</h5>
          <JaxCkbContent evidenceType="evidence" items={evidence} />
        </div>
      )}
      {extendedEvidence && extendedEvidence.length && (
        <div className="tw-mt-8">
          <h5>Extended Evidence Results</h5>
          <JaxCkbContent
            evidenceType="extended-evidence"
            items={extendedEvidence}
          />
        </div>
      )}
      <div className="varsome-text-muted tw-mt-8">
        <small>
          <i>
            Somatic gene variant annotations and related content have been
            powered by The Jackson Laboratory Clinical Knowledgebase (JAX-CKB™).
          </i>
        </small>
      </div>
    </div>
  );
  const url = getVarsoUrl("https://ckb.jax.org/");
  return (
    <div className="varsome-result-fixed">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          version={jaxVersion}
          url={url}
          titleDetailsRight={titleDetailsRight}
          content={content}
          title="JAX CKB"
        />
      </div>
    </div>
  );
};

JaxCkbComponent.propTypes = {
  jaxVersion: PropTypes.string,
  jaxData: PropTypes.shape({
    coding_impact: PropTypes.string,
    protein_effect: PropTypes.string,
    evidence: PropTypes.arrayOf(
      PropTypes.shape({
        pub_med_references: PropTypes.arrayOf(PropTypes.number),
        therapy_id: PropTypes.number,
        therapy: PropTypes.string,
        response_type: PropTypes.string,
        normalized_drug: PropTypes.string,
        approval_status: PropTypes.string,
        amp_tier: PropTypes.string,
        normalized_cancer: PropTypes.string,
        cap_asco_evidence_level: PropTypes.string,
        indication: PropTypes.string,
        efficacy_evidence: PropTypes.string,
        evidence_type: PropTypes.string,
      })
    ),
    extended_evidence: PropTypes.arrayOf(
      PropTypes.shape({
        therapy_id: PropTypes.number,
        therapy: PropTypes.string,
        response_type: PropTypes.string,
        normalized_drug: PropTypes.string,
        approval_status: PropTypes.string,
        amp_tier: PropTypes.string,
        normalized_cancer: PropTypes.string,
        cap_asco_evidence_level: PropTypes.string,
        indication: PropTypes.string,
        efficacy_evidence: PropTypes.string,
        evidence_type: PropTypes.string,
      })
    ),
    ckb_id: PropTypes.number,
    gene_variant_descriptions: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        pub_med_references: PropTypes.arrayOf(PropTypes.number),
      })
    ),
  }),
};

JaxCkbComponent.defaultProps = {
  jaxVersion: null,
  jaxData: null,
};

const mapStateToProps = (state) => {
  return {
    jaxVersion: getJaxVersion(state),
    jaxData: getJaxData(state),
  };
};

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorGuard(JaxCkbComponent, "JaxCkbComponent", "JAX CKB"));
